// islands.tsx — the single build entry. Loaded by {{ vite_assets() }}.
//
// Register each island here, keyed by its data-island name. A page drops a
// placeholder (<div data-island="field-access"></div>) and mount.tsx wires the
// matching component in.
//
// Islands are CODE-SPLIT: each entry is a dynamic import() thunk, not a static
// import, so Rollup emits one chunk per island and the browser fetches it only
// when a page contains that placeholder. This thin entry (registry + mount +
// tokens) is all that loads up front. `pick` adapts a named export to the
// { default } shape React.lazy expects; the import() argument must stay a
// literal string so the bundler can statically find and split it.
import '../ui/tokens.css'
import { mountIslands, type IslandComponent, type IslandRegistry } from '../lib/mount'
import { installIconTips } from '../ui/iconTips'

// Icon-only buttons show their label in the shared tip, on every page that
// loads this entry, server-drawn or island.
installIconTips()

function pick(p: Promise<Record<string, unknown>>, name: string): Promise<{ default: IslandComponent }> {
  return p.then((m) => ({ default: m[name] as IslandComponent }))
}

const registry: IslandRegistry = {
  // Organiser QR access-code modal. Driven imperatively via window.faCreateCode
  // / window.faOpenCodes, which the component publishes on mount.
  'field-access': () => pick(import('../features/field-access/FieldAccessPanel'), 'FieldAccessPanel'),
  // Public ward history line chart (no-login /elections pages, embedded props).
  'public-ward-history': () =>
    pick(import('../features/public-elections/WardHistoryChart'), 'WardHistoryChart'),
  // Organiser live map of opted-in door-knockers (polls /canvass/live_data).
  'canvass-live-map': () => pick(import('../features/canvass-live/CanvassLiveMap'), 'CanvassLiveMap'),
  // Leafleter deliverer round view (map + door list + locate/share + status).
  'leaflet-round': () => pick(import('../features/leaflet-round/RoundView'), 'RoundView'),
  // Insights: ward demographics table (column picker, sort, search).
  'demographics-table': () => pick(import('../features/insights-table/DemographicsTable'), 'DemographicsTable'),
  // Electoral Calculus map across wards or output areas, shaded by tribe or axis.
  'insights-electoral-map': () =>
    pick(import('../features/insights-electoral-map/ElectoralMap'), 'ElectoralMap'),
  // Insights › Ad spend: rivals' estimated Meta ad spend by ward, constituency
  // or council, and the ads aimed at each.
  'insights-ad-spend': () => pick(import('../features/insights-ad-spend/AdSpendMap'), 'AdSpendMap'),
  'insights-projections': () => pick(import('../features/insights-projections/Projections'), 'Projections'),
  // Insights election results: one ward's full results, a constituency's,
  // the Results table and the Trends chart (every view but the map).
  // Recent activity's Answers: the day's scores or core answers as share
  // rows, or the Summary's flow from before to the day (props from the page).
  'recent-answers': () => pick(import('../features/recent-activity/RecentAnswers'), 'RecentAnswers'),
  // Recent activity's people panel: the electors behind a figure clicked.
  'recent-people': () => pick(import('../features/recent-activity/RecentPeople'), 'RecentPeople'),
  'election-results': () =>
    pick(import('../features/election-results/ElectionResults'), 'ElectionResults'),
  // Ward/branch Core party list: draggable pills, register picker, per-entry
  // code and colour overrides.
  'core-party-editor': () =>
    pick(import('../features/core-party-editor/CorePartyEditor'), 'CorePartyEditor'),
  // Insights explorer — single-page ward + output-area drill-down with census
  // and party strength layers (flag insights_explorer).
  'insights-explorer': () => pick(import('../features/insights-explorer/Explorer'), 'Explorer'),
  // Doorstep recruit-volunteer modal (first slice of the canvass field view).
  'recruit-modal': () => pick(import('../features/canvass-recruit/RecruitModal'), 'RecruitModal'),
  // Doorstep "note casework" modal — sibling of recruit-modal; creates a case
  // linked to the elector via the canvass casework endpoint.
  'casework-modal': () => pick(import('../features/canvass-casework/CaseworkModal'), 'CaseworkModal'),
  // Doorstep action sheet — sibling of recruit-modal; signs an elector up to a
  // petition/letter/event/form from the door, or hands off to a QR code for
  // anything the elector has to complete on their own phone. Triggered by the
  // action/volunteer/casework doorstep-form blocks via window.openActionSheet.
  'action-sheet': () => pick(import('../features/canvass-actions/ActionSheet'), 'ActionSheet'),
  // Casework working surface — List / Board / Timeline of cases, the create-case
  // form and the case detail panel with its comment thread.
  'casework-board': () => pick(import('../features/casework-board/CaseworkBoard'), 'CaseworkBoard'),
  // Ward board: a branch's wards in two columns, dragged between target and non-target.
  'ward-board': () => pick(import('../features/ward-board/WardBoard'), 'WardBoard'),
  // Read-only organiser-notes + round-script modals (field view).
  'notes-script': () => pick(import('../features/canvass-notes/NotesScriptModals'), 'NotesScriptModals'),
  // Doorstep field view core — roads / household / elector + offline save queue,
  // locks and live-sync (full React rewrite of model + view; map stays separate).
  'canvass-field': () => pick(import('../features/canvass-field/FieldView'), 'CanvassField'),
  // Ward "Data entry" pane — desktop typing-up of paper canvass sheets. Shares
  // the road/address row design; saves are campaign-scoped and back-dateable.
  'canvass-data-entry': () =>
    pick(import('../features/canvass-data-entry/CanvassDataEntry'), 'CanvassDataEntry'),
  // Doorstep forms: the door-knocking tab's sub-page. Forms, the branch's
  // question library and, at an area, its answers, scoped by the Area picker.
  'doorstep-forms-page': () =>
    pick(import('../features/doorstep-forms-page/DoorstepFormsPage'), 'DoorstepFormsPage'),
  // Phonebank call screen — load next contact, call, record outcome, advance.
  'phonebank-call': () => pick(import('../features/phonebanking-call/PhonebankCall'), 'PhonebankCall'),
  // Phonebank campaign detail — header, stats, overview / call list / call log
  // tabs, and the edit + upload-list modals (organiser overview page).
  'phonebank-detail': () => pick(import('../features/phonebanking-detail/PhonebankDetail'), 'PhonebankDetail'),
  'phonebank-edit': () => pick(import('../features/phonebanking-detail/EditPage'), 'EditPage'),
  // Activists CRM Contacts tab — searchable / sortable / filterable contact
  // table, pagination, add/edit/delete, custom fields and CSV import. Owns the
  // Contacts tab only; the other tabs on the page stay as inline JS.
  'activists-list': () => pick(import('../features/activists-list/ActivistsList'), 'ActivistsList'),
  // Electors list: the register on the shared people-list frame (view tabs,
  // search and filter, the shared elector panel). Supporters is activists-list.
  'electors-list': () => pick(import('../features/people-list/ElectorsList'), 'ElectorsList'),
  'addresses-list': () => pick(import('../features/addresses-list/AddressesList'), 'AddressesList'),
  // Filters section of the Electors and Supporters pages.
  'saved-views': () => pick(import('../features/people-list/SavedViews'), 'SavedViews'),
  // Imports and exports section of the Supporters page.
  'supporter-imports': () =>
    pick(import('../features/supporter-imports/SupporterImports'), 'SupporterImports'),
  // Broadcast email (P0.4): the full compose builder (canvas + audience +
  // settings + send).
  'email-builder': () => pick(import('../features/broadcast-email/EmailBuilder'), 'EmailBuilder'),
  // Campaign report analytics: per-link clicks, activity chart, client and
  // device breakdown (email parity phase 1).
  'report-analytics': () => pick(import('../features/broadcast-email/ReportAnalytics'), 'ReportAnalytics'),
  // Side-by-side comparison of sent broadcasts (email parity phase 7).
  'email-compare': () => pick(import('../features/broadcast-email/EmailCompare'), 'EmailCompare'),
  // Org email health dashboard: deliverability, bounce classes, domains,
  // list growth (email parity phase 7).
  'email-health': () => pick(import('../features/broadcast-email/EmailHealth'), 'EmailHealth'),
  // Journeys (automation builder, phase 1): a linear step-list editor —
  // trigger, then an ordered list of actions — plus publish/pause/resume
  // and a manual-enrol contacts panel. Graph canvas is phase 2.
  'journey-editor': () => pick(import('../features/journeys/JourneyEditor'), 'JourneyEditor'),
  // Customisable campaign dashboard: a drag-and-drop grid of widgets across
  // user-named pages, with a page-wide time filter, shared + personal layouts
  // and importable org templates. Supersedes the five fixed dashboard islands.
  'ward-dashboard': () => pick(import('../features/ward-dashboard/WardDashboard'), 'WardDashboard'),
  // Campaigning, Volunteers: the Overview's old Volunteers view on its own page.
  'ward-volunteers': () => pick(import('../features/ward-dashboard/VolunteersPage'), 'VolunteersPage'),
  // The user's home dashboard for assigned work: active-assignment cards, the
  // recently-completed list, the organiser 'new assignment' modal (cascading
  // ward / round selectors, paper / QR modes), the mark-done undo snackbar and
  // the new-assignments poll. Actions stay as server form POSTs; QR bridges to
  // the field-access panel via window.faCreateCode.
  'assignments-index': () => pick(import('../features/assignments-index/AssignmentsIndex'), 'AssignmentsIndex'),
  // Election-results verification workflow — ballot picker, dashboard (KPIs,
  // Bar chart with prediction error bars, assumptions, PD breakdown),
  // tally-sheet preview/PDF, and the organiser data-entry form. Backs both the
  // standalone verification page and the lazy-loaded campaign-detail pane.
  'campaign-verification': () => pick(import('../features/campaign-verification/CampaignVerification'), 'CampaignVerification'),
  // Actions page — list/manager of actions (events, event groups, forms,
  // petitions) with search / sort / pin / bulk select, plus the branding tab
  // (custom HTML/CSS wrappers and an inline editor with new-tab live preview).
  'activist-actions': () => pick(import('../features/activist-actions/ActionsPage'), 'ActionsPage'),
  // Supporters › Brands: saved looks (logo, colours, fonts, buttons, social
  // links) for emails and public pages, and one brand's editor.
  'brands-page': () => pick(import('../features/brands-page/BrandsPage'), 'BrandsPage'),
  // Email-campaign editor: the Recipient & message tab (target scope, fixed
  // recipients, subject pool, issue-variant "asks"). Writes hidden inputs the
  // server re-parses, alongside the shared CanvasPane page-builder.
  'email-campaign-config': () => pick(import('../features/activist-email-campaigns/EmailCampaignConfig'), 'EmailCampaignConfig'),
  // Public "email your representative" flow: collect details, resolve the rep by
  // postcode, edit a pre-filled message, then open it in the supporter's own
  // email client (mailto / Gmail / Outlook). First island on the public flow.
  'email-campaign-action': () => pick(import('../features/public-email-campaign/EmailCampaignAction'), 'EmailCampaignAction'),
  // Email-campaign detail / overview — header actions, stat cards, and the
  // analytics / senders / tracking-links tabs. Read-only data arrives in
  // data-props; mutations (duplicate, archive, delete) stay server form POSTs.
  'email-campaign-detail': () => pick(import('../features/activist-email-campaigns/EmailCampaignDetail'), 'EmailCampaignDetail'),
  // Quiz editor: the Questions & result tab (result mode, question builder,
  // outcome/band/comparison editors, capture gate, sandboxed custom HTML with
  // the copy-instructions-for-an-LLM authoring path). Writes the quiz_config
  // hidden input the server re-parses.
  'quiz-config': () => pick(import('../features/activist-quizzes/QuizConfig'), 'QuizConfig'),
  // Ticketed-event editor: the Tickets tab (ticket types with scheduled
  // prices, capacity pools, discount codes, attendee detail mode). Writes the
  // ticket_setup hidden input the server re-parses.
  'ticketed-event-config': () => pick(import('../features/activist-ticketed-events/TicketedEventConfig'), 'TicketedEventConfig'),
  // Public ticket shop: quantities, discount code, buyer + attendee details,
  // then the Stripe Payment Element (direct charge on the org's connected
  // account). Free orders and approval requests skip payment.
  'ticketed-event-action': () => pick(import('../features/public-ticketed-events/TicketedEventAction'), 'TicketedEventAction'),
  // Ticketed-event detail: revenue KPIs, the orders table with approve /
  // decline / cancel / refund / resend, requests queue, attendees, ticket
  // types and discount codes.
  'ticketed-event-detail': () => pick(import('../features/activist-ticketed-events/TicketedEventDetail'), 'TicketedEventDetail'),
  // Public quiz flow: intro, question stepper, platform-rendered sign-up gate,
  // result screen. Hosts the sandboxed iframe in custom-HTML mode and owns the
  // postMessage bridge, so quiz content never touches data capture.
  'quiz-action': () => pick(import('../features/public-quiz/QuizAction'), 'QuizAction'),
  // Name-origin classifier at /tools/name-origin. Two tabs: look up names
  // against the global dictionary, and label register names to build it.
  // Reachable by URL only, no nav entry.
  'name-origin': () => pick(import('../features/name-origin/NameOrigin'), 'NameOrigin'),
  // Event detail / overview — header actions, public-URL bar, stat cards, and
  // the analytics / attendees / tracking-links / check-in-helpers tabs.
  // Read-only data arrives in data-props; mutations (duplicate, archive,
  // delete, assign helper) stay server form POSTs.
  'event-detail': () => pick(import('../features/activist-events/EventDetail'), 'EventDetail'),
  // The Lead on the event form: the calendar's person picker, posting
  // lead_user_id with the form.
  'event-lead': () => pick(import('../features/activist-events/LeadField'), 'LeadField'),
  // Standalone helper check-in screen (event_checkin.html). Three tabs:
  // Scan QR code / Check in manually / Add a walk-in.
  'checkin-panel': () => pick(import('../features/activist-events/CheckinPanel'), 'CheckinPanel'),
  // Petition detail / overview — header actions, public-URL bar, stat cards,
  // and the analytics / signatories / tracking-links tabs.
  // Read-only data arrives in data-props; mutations stay server form POSTs.
  'petition-detail': () => pick(import('../features/activist-petitions/PetitionDetail'), 'PetitionDetail'),
  // Event group detail / overview — header actions, public-URL bar, stat cards,
  // and the analytics / sign-ups / tracking-links tabs, scoped to activity
  // attributed to the group's own public page (see source_event_group_id).
  // Read-only data arrives in data-props; mutations stay server form POSTs.
  'event-group-detail': () => pick(import('../features/activist-event-groups/EventGroupDetail'), 'EventGroupDetail'),
  // Form detail / overview — header actions, public-URL bar, stat cards,
  // and the results / analytics / responses / tracking-links tabs.
  // Read-only data arrives in data-props; mutations stay server form POSTs.
  'form-detail': () => pick(import('../features/activist-forms/FormDetail'), 'FormDetail'),
  // People, Filters — the one page for every saved filter. One population,
  // a scope list filter, the shared recursive AND/OR/NOT filter builder
  // (features/filter-builder) and the one person pack.
  'filters-page': () => pick(import('../features/filters-page/FiltersPage'), 'FiltersPage'),
  'filter-people': () => pick(import('../features/filters-page/FilterPeople'), 'FilterPeople'),
  // Action create/edit page builder — the visual canvas (rows / columns /
  // blocks, rich text, image upload + crop, drag/sort) shared by the event,
  // petition and form create+edit pages. The host <form> stays a classic
  // multipart POST; this island writes the layout_config / field_config inputs.
  'action-canvas-pane': () => pick(import('../features/activist-action-forms/CanvasPaneIsland'), 'CanvasPaneIsland'),
  // Fundraiser management page — header actions, stat cards (raised / count /
  // average / target), and the donations + tracking-links tabs. Mutations
  // (duplicate, archive, delete) stay server form POSTs; publishing is on the
  // editor. Mirrors the form/petition/event detail islands.
  'fundraiser-detail': () => pick(import('../features/activist-fundraisers/FundraiserDetail'), 'FundraiserDetail'),
  // Fundraiser editor "Donation amounts" control — mode (fixed / suggested /
  // products), per-page currency, the add/blank-custom/drag/remove suggested-
  // amounts builder and the products builder. Writes hidden inputs the server
  // re-parses, alongside the shared CanvasPane page-builder.
  'fundraiser-amounts': () => pick(import('../features/fundraiser-amounts/FundraiserAmounts'), 'FundraiserAmounts'),
  // Action create/edit CRM sign-up section — sign-up tag picker + exclude
  // switch, shared by all 8 action editors. Writes an_signup_tags / crm_exclude
  // into the host classic POST.
  'action-signup-section': () => pick(import('../features/activist-action-forms/SignupSectionIsland'), 'SignupSectionIsland'),
  // Campaign detail Posters tab — owns all three sub-tabs (Map, request List,
  // Assignments round list) plus both modals (New poster round, Add request).
  // Lazy-loads once the posters pane is the active .dg-tab-pane.
  'campaign-posters': () => pick(import('../features/campaign-posters/PostersPane'), 'PostersPane'),
  'group-posters-extras': () =>
    pick(import('../features/campaign-posters/GroupPostersExtras'), 'GroupPostersExtras'),
  'group-posters-map-layers': () =>
    pick(import('../features/campaign-posters/GroupPostersMapLayers'), 'GroupPostersMapLayers'),
  // Campaign detail door-knocking tab → Assignments sub-pane. Lists active /
  // completed / revoked door-knock assignments, revokes (with an undo
  // snackbar) and serves the paper-pack PDFs (download / print / share).
  // Publishes window.cvsReloadAssignments so the inline sub-tab switcher and
  // modal refetch the list after a change.
  'campaign-assignments': () => pick(import('../features/campaign-assignments/CampaignAssignments'), 'CampaignAssignments'),
  // Polling day, the Dashboard, for a ward and for an area group: a countdown,
  // the group being got out as tiles and by the hour, and polling stations
  // with their teller lanes or the area's wards. First paint comes from
  // data-props, then it asks again every 30s
  // (docs/design/polling-day-screens.md, round 2).
  'gotv-dashboard': () => pick(import('../features/gotv-dashboard/GotvDashboard'), 'GotvDashboard'),
  // Tellers page for a ward on polling day: the rota as one lane per polling
  // station, with a box on the lane to add or change a shift
  // (docs/design/polling-day-screens.md, round 2).
  'gotv-tellers': () => pick(import('../features/gotv-tellers/GotvTellers'), 'GotvTellers'),
  // A volunteer's Cover a polling station page: the Tellers page's lanes,
  // read-only, and a form to claim cover that a gap fills in.
  'gotv-cover': () => pick(import('../features/gotv-cover/GotvCover'), 'GotvCover'),
  // Polling day map for one ward: doors still to knock, live figures and
  // polling districts, polled every 30s (docs/design/polling-day-map.md).
  'polling-day-map': () => pick(import('../features/polling-day-map/PollingDayMap'), 'PollingDayMap'),
  // Campaign detail Rounds tab — owns both sub-tabs (Map and Households). Draws /
  // edits / imports / saves round boundaries on a MapLibre map, lists the rounds,
  // and runs the Excel / PDF / round-set exports. Built on first reveal so
  // MapLibre never paints into a hidden container. Bridges the inline
  // door-knocking tab via window.rRounds / rRenderTable / cvsOpenBulkPdfForRound
  // and consumes window._scrScripts / _scrLoad.
  'campaign-rounds': () => pick(import('../features/campaign-rounds/RoundsPanel'), 'RoundsPanel'),
  // Shared round tile grid — door-knocking Overview and leafleting Overview.
  // One component for both activities (see frontend/src/features/round-tile-grid).
  'round-tile-grid': () => pick(import('../features/round-tile-grid/RoundTileGrid'), 'RoundTileGrid'),
  // A leaflet drop's rounds for the organiser: door-knocking's list and map.
  'leaflet-rounds': () => pick(import('../features/leaflet-rounds/LeafletRounds'), 'LeafletRounds'),
  // Door-knocking for one ward: the map and the list an organiser assigns
  // from, on any of the ward's round sets or the whole ward. Takes the round
  // grid's place on the ward page (the grid stays for leaflet drops, the hub
  // and area groups).
  'door-knocking-rounds': () => pick(import('../features/door-knocking/RoundsPage'), 'RoundsPage'),
  // The same for an area group or All areas: its wards as areas, and a ward's
  // rounds in place once it is opened.
  'door-knocking-wards': () => pick(import('../features/door-knocking/WardsPage'), 'WardsPage'),
  // Who is out now, stalled, not out today, done and free in a ward: the
  // People tab, in place of Assignments for an organiser.
  'door-knocking-people': () => pick(import('../features/door-knocking/PeoplePage'), 'PeoplePage'),
  // Ward door-knocking overview: core and score as they stood at fixed
  // distances before polling day, for the ward's target contest.
  'contest-trend': () => pick(import('../features/contest-trend/ContestTrend'), 'ContestTrend'),
  // People overview: new supporters by month, split by type (stacked bars).
  'supporter-growth-chart': () => pick(import('../features/supporter-growth/GrowthChart'), 'GrowthChart'),
  // Print prices › Price history: each printer's price over time.
  'print-price-history': () => pick(import('../features/print-price-history/PriceHistory'), 'PriceHistory'),
  // Action days: the day itself, and Bulk assign for big days (teams,
  // meeting points and rounds; allocated days run the Plan / Check-in / Live
  // workspace, and Live polls /live every 30 seconds).
  'action-day': () => pick(import('../features/action-day/ActionDay'), 'ActionDay'),
  // Campaigning › Action days › Plan action day: the dialog that makes a day.
  'action-day-plan': () =>
    pick(import('../features/action-day-plan/PlanActionDay'), 'PlanActionDay'),
  // Campaigning › Calendar: election dates, leaflets, action days and events,
  // and planned comms for the chosen area, as Month, Week, Day, Timeline or
  // List (the page strip's data-cal-view buttons switch them).
  'campaign-calendar': () =>
    pick(import('../features/campaign-calendar/CampaignCalendar'), 'CampaignCalendar'),
  // Calendar › Plan a by-election: a ward's seat, the window the rules give
  // for its polling day (or none, inside the six months), and the timetable.
  // Electoral paperwork (docs/design/electoral-paperwork.md): one
  // candidate's papers, Add an election, and an election's settings.
  'paperwork-candidate': () => pick(import('../features/paperwork/CandidateForm'), 'CandidateForm'),
  'paperwork-add-election': () => pick(import('../features/paperwork/AddElection'), 'AddElection'),
  'paperwork-election-settings': () =>
    pick(import('../features/paperwork/ElectionSettings'), 'ElectionSettings'),
  'by-election-planner': () =>
    pick(import('../features/by-election-planner/ByElectionPlanner'), 'ByElectionPlanner'),
  // A leaflet drop's Stages and Print on its page, for the drop's managers,
  // and the Duplicate button beside Edit drop (campaign calendar, piece 2).
  'leaflet-drop-plan': () => pick(import('../features/leaflet-drop-plan/DropPlan'), 'DropPlan'),
  'leaflet-drop-duplicate': () =>
    pick(import('../features/leaflet-drop-plan/DuplicateDropButton'), 'DuplicateDropButton'),
  // Map of all rounds beside Download all round PDFs (docs/design/drop-rounds-map.md).
  'leaflet-drop-map': () => pick(import('../features/rounds-map/RoundsMapButton'), 'RoundsMapButton'),
  // Global search bar (top bar, every page) — pages resolve client-side from the
  // injected list; campaigns + people come from /search/suggest. People show for
  // organisers/admins only; they also get an add-a-contact action.
  'global-search': () => pick(import('../features/global-search/GlobalSearch'), 'GlobalSearch'),
  // Search results page: the search, a filter panel whose tick boxes each
  // carry the search they lead to, and tables of people and addresses.
  'search-results': () => pick(import('../features/search-results/SearchResults'), 'SearchResults'),
  // Elector management page — header, add-note composer, unified activity
  // timeline (canvass/GOTV/voting/posters/register changes/notes), and the
  // admin-only register-edit modal.
  'elector-manager': () => pick(import('../features/elector-manager/ElectorManager'), 'ElectorManager'),
  // Published donation review queue (Register hub → Published donations). Four
  // tabs over one JSON endpoint, bulk confirm of the likely tier, and the
  // hand-match picker on the Could not match tab.
  'donor-match-review': () =>
    pick(import('../features/donor-match-review/DonorMatchReview'), 'DonorMatchReview'),
  // Address management page — the address plus the org's electors living there,
  // each linking to their elector management page.
  'address-manager': () => pick(import('../features/address-manager/AddressManager'), 'AddressManager'),
  // Road (street) records page, every property on a street with inline
  // score/party/flag entry per resident, grouped by address.
  'road-manager': () => pick(import('../features/road-manager/RoadManager'), 'RoadManager'),
  // CRM person page — a contact (activist) not necessarily on the register, with
  // links to their register record and volunteer activity where they exist.
  'supporter-page': () => pick(import('../features/supporter-page/SupporterPage'), 'SupporterPage'),
  'link-compare': () => pick(import('../features/link-compare/LinkCompare'), 'LinkCompare'),
  // Electors, Matching — the queue of supporters still to match to the
  // register, the links that exist and the decisions set aside.
  matching: () => pick(import('../features/matching/Matching'), 'Matching'),
  // Message testing — the tabbed section (Tests / Personas / Areas), the New
  // test flow and area-centric panel management. Replaces the old inline
  // vanilla-JS page; all UI is this one island.
  'message-testing': () => pick(import('../features/message-testing/MessageTestingApp'), 'MessageTestingApp'),
  // Round detail page: tile-free boundary outline for the round being viewed.
  'round-boundary-map': () => pick(import('../features/round-boundary-map/RoundBoundaryMap'), 'RoundBoundaryMap'),
  // The shared geographic map. One component behind every map surface: the
  // host page passes a geography scope and the views it wants, and
  // /api/maps/data resolves and access-checks the scope.
  'geo-map': () => pick(import('../features/geo-map/GeoMap'), 'GeoMap'),
  // The one area picker (ui/AreaPicker.tsx) on a server-rendered page, through
  // the area_picker macro in templates/macros/_area_picker.html.
  'area-picker': () => pick(import('../features/area-picker/AreaPickerIsland'), 'AreaPickerIsland'),
}

// Expose a re-scan so islands injected after first paint get mounted. The
// campaign detail page lazy-loads the verification pane as an HTML fragment via
// fetch and inserts it after DOMContentLoaded; it calls this to mount the
// placeholder inside that fragment. mountIslands skips already-mounted nodes,
// so a re-scan is cheap and idempotent.
declare global {
  interface Window {
    mountIslands?: () => void
  }
}
window.mountIslands = () => mountIslands(registry)

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountIslands(registry))
} else {
  mountIslands(registry)
}
